import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/dsp/api')

/** 车辆状态：idle 可被智能派车装货；loading 已挂未出发的行车任务；maintenance 不参与派车 */
export type VehicleStatus = 'idle' | 'loading' | 'running' | 'maintenance'

/** 车辆类型 */
export type VehicleType = 'van' | 'truck' | 'heavy' | 'tractor'

/** 车辆档案。字段与 services/dsp/internal/vehicle/model.go 的 Vehicle 一一对应。 */
export interface Vehicle {
  id: number
  tenant_id: number
  plate_no: string
  brand: string
  type: VehicleType
  /** 额定载重（kg） */
  capacity_kg: number
  /** 额定容积（cm³） */
  capacity_cm3: number
  status: VehicleStatus
  /** 绑定司机；0 表示未绑定 */
  driver_id: number
  created_at: string
  updated_at: string
}

/** 列表行：车辆 + 司机姓名 */
export interface VehicleRow extends Vehicle {
  driver_name: string
}

export interface VehicleInput {
  plate_no: string
  brand?: string
  type: VehicleType
  capacity_kg: number
  capacity_cm3: number
  status: VehicleStatus
  /** 未绑定时省略（后端收 0） */
  driver_id?: number
}

export interface VehicleQuery extends PageQuery {
  keyword?: string
  status?: string
  type?: string
}

/** 与后端 services/dsp/internal/vehicle/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<VehicleStatus, string> = {
  idle: '空闲',
  loading: '装货中',
  running: '在途',
  maintenance: '维保中',
}

/** 与后端 services/dsp/internal/vehicle/model.go 的 TypeNames 同源两份 */
export const TYPE_LABELS: Record<VehicleType, string> = {
  van: '厢式货车',
  truck: '载货车',
  heavy: '重卡',
  tractor: '牵引车',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<VehicleStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  idle: 'success',
  loading: 'primary',
  running: 'warning',
  maintenance: 'info',
}

export const listVehicles = (q: VehicleQuery): Promise<PageResult<VehicleRow>> =>
  api.get<PageResult<VehicleRow>>('/vehicles', { ...q })

export const createVehicle = (input: VehicleInput): Promise<Vehicle> => api.post<Vehicle>('/vehicles', input)

export const updateVehicle = (id: number, input: VehicleInput): Promise<Vehicle> =>
  api.put<Vehicle>(`/vehicles/${id}`, input)

export const deleteVehicle = (id: number): Promise<void> => api.del<void>(`/vehicles/${id}`)

/** 下拉参照用的全量车辆：后端 size 上限 200 */
export const listVehicleOptions = (): Promise<VehicleRow[]> =>
  listVehicles({ page: 1, size: 200 }).then((page) => page.list)
